/**
 * Canonical, presentation-agnostic descriptions of captured session telemetry.
 *
 * One source of truth for "what does this event/page-state mean and where did
 * it happen", consumed by every text surface that renders raw session data —
 * the MCP `get_test_artifacts` tool, the CLI, and the dashboard's export/download
 * paths. Each function returns only the *detail* (no timestamp, no type label);
 * callers prepend their own time formatting, which differs by surface (absolute
 * wall-clock vs. locale time vs. video-relative offset).
 *
 * `urlOfEvent` is the structured accessor the renderers are built on: it answers
 * "which page was the tester on at this event" without going through a display
 * string, so pipeline code can correlate a finding's timestamp to a URL (and, by
 * extension, a session recording) without re-deriving the union-to-URL mapping.
 */

import type { PlaywrightEvent, PageStateSnapshot } from './session-events.types.js';

/**
 * The full URL a navigation event landed on, including query string, or
 * `undefined` for events that don't carry one (clicks, typing, form submits).
 * Query strings matter: SPAs like PostHog keep essential state there
 * (`?sessionRecordingId=...`), so pathname alone is not enough.
 */
export function urlOfEvent(event: PlaywrightEvent): string | undefined {
  switch (event.type) {
    case 'page_load':
    case 'spa_route_change':
    case 'tab_change':
      return event.url;
    case 'click':
    case 'form_submit':
    case 'keyboard_input':
      return undefined;
  }
}

/** Human-readable detail for one event — the meaningful payload, not just its type. */
export function describeEvent(event: PlaywrightEvent): string {
  switch (event.type) {
    case 'click':
      return `Click at (${event.x}, ${event.y})${event.element ? ` on ${event.element}` : ''}`;
    case 'page_load':
      return `${event.event}: ${event.url}`;
    case 'form_submit': {
      const { formInfo } = event;
      const target = formInfo.action ? `, action=${formInfo.action}` : '';
      return `Form submit (${formInfo.fieldCount} field${formInfo.fieldCount === 1 ? '' : 's'}${target})`;
    }
    case 'spa_route_change':
      return `Navigate to ${event.url}`;
    case 'keyboard_input': {
      const { elementInfo } = event;
      const field = elementInfo.name ? `${elementInfo.tagName}[name=${elementInfo.name}]` : elementInfo.tagName;
      return `Typed "${event.value}" in ${field}`;
    }
    case 'tab_change':
      return `Tab: ${event.title.length > 0 ? event.title : event.url}`;
  }
}

/** Recursively counts accessibility nodes (objects carrying a string `role`). */
function countAccessibilityNodes(node: unknown): number {
  if (Array.isArray(node)) {
    return node.reduce<number>((sum, child) => sum + countAccessibilityNodes(child), 0);
  }
  if (node !== null && typeof node === 'object') {
    const record = node as Record<string, unknown>;
    const self = typeof record.role === 'string' ? 1 : 0;
    return self + countAccessibilityNodes(record.children);
  }
  return 0;
}

/**
 * Compact one-line summary of an accessibility tree — the tree can be large, so
 * text surfaces render this instead of the full JSON blob (available via the raw
 * artifact endpoint when the full tree is actually needed).
 */
export function summarizeAccessibilityTree(tree: Record<string, unknown>): string {
  const count = countAccessibilityNodes(tree);
  const rootRole = typeof tree.role === 'string' ? tree.role : undefined;
  const rootName = typeof tree.name === 'string' ? tree.name : undefined;
  const root = rootRole ? `${rootRole}${rootName ? ` "${rootName}"` : ''}` : 'a11y tree';
  return `${root}, ${count} node${count === 1 ? '' : 's'}`;
}

/**
 * One-line detail for a page-state snapshot: what triggered it and the full URL,
 * with the accessibility tree summarized by default. Pass `includeTree` to embed
 * the full tree JSON (used by download/export surfaces, not compact LLM output).
 */
export function describePageState(
  // Only the fields the description reads — so both the raw snapshot and its
  // normalized (video-relative) counterpart, which differ only in `timestamp`,
  // satisfy it.
  state: Pick<PageStateSnapshot, 'trigger' | 'url' | 'accessibilityTree'>,
  opts: { includeTree?: boolean } = {},
): string {
  const header = `[${state.trigger}] ${state.url}`;
  if (opts.includeTree) {
    return `${header}\n${JSON.stringify(state.accessibilityTree, null, 2)}`;
  }
  return `${header} (${summarizeAccessibilityTree(state.accessibilityTree)})`;
}
